import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface User {
  email: string;
  name: string;
  usertype: string;
  address: string;
  country: string;
  email_verified_at: string;
  first_name: string;
  id: number
  image: string;
  last_name: string;
  phone: string;
  state: string;
  bvn: string;
  nimc: string;
  Profile: string;
  AllMenuAccess: boolean;
  role: string;
  PENS_SCOPE: 'pfa' | 'pfc' | 'others'
  employerCode?: string;
  defaultEmployerCode?: string;
}

interface IWorkFlow {
  "id": number,
  "name": string,
  "createdAt": string,
  "workStep": "Validator" | "Approval",
  "workStepLevel": number,
  "createdBy": string,
  "updatedBy": string,
  "updatedAt": string,
  "description": string,
  "quorum": number,
  "users": [
    {
      "emailAddress": string,
      "firstName": string,
      "lastName": string,
      "phoneNumber": string,
      "roles": [
        string
      ],
      "viewOrDownloadEmployerSchedules": boolean,
      "userId": string,
      "dateCreated": string,
      "lastLogin": string,
      "enable2Fa": boolean,
      "address": string
    }
  ]
}

interface IAppConfig {
  notifyLoginActivity: boolean,
  enableTwoFactorAuthentication: boolean,
  securityMailBox: string,
  singleUserModeEnabled: boolean,
  enabledCustomApprovalFlow: boolean
}


interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  showVerificationPending: boolean;
  ledger: Record<string, number>;
  bvnDetails: Record<string, any> | null;
  referrals: Record<string, any> | null;
  selectedProductObj: Record<string, string>;
  workflows: IWorkFlow[] | null;
  appConfig: IAppConfig | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  showVerificationPending: false,
  ledger: {},
  bvnDetails: null,
  referrals: null,
  selectedProductObj: {},
  workflows: [],
  appConfig: null,
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<User>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      localStorage.setItem("PenUser", JSON.stringify(action.payload));
    },
    setShowVerificationPending: (state, action: PayloadAction<boolean>) => {
      state.showVerificationPending = action.payload;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.showVerificationPending = false;
      state.ledger = {};
      state.bvnDetails = null;
      localStorage.removeItem("PenUser");
    },
    update: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      localStorage.setItem("user", JSON.stringify(action.payload));
    },
    updateUserType: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.usertype = action.payload;
      }
    },
    updateLedger: (state, action: PayloadAction<Record<string, number>>) => {
      if (state.user) {
        state.ledger = action.payload;
      }
    },
    setBVNDetails: (state, action) => {
      state.bvnDetails = action.payload;
    },
    setRefferals: (state, action) => {
      state.referrals = action.payload;
    },
    setProduct: (state, action) => {
      state.selectedProductObj = action.payload;
    },
    setWorkflows: (state, action) => {
      state.workflows = action.payload;
    },
    setAppConfig: (state, action) => {
      state.appConfig = action.payload;
    },
    setActiveEmployerCode: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.employerCode = action.payload;
      }
    }
  },
})

export const {
  login,
  logout, 
  update,
  updateUserType,
  setShowVerificationPending,
  updateLedger,
  setBVNDetails,
  setRefferals,
  setProduct,
  setWorkflows,
  setAppConfig,
  setActiveEmployerCode,
} = authSlice.actions;

// Selectors
export const selectActiveEmployerCode = (state: any) => {
  return state.auth.user?.employerCode || state.auth.user?.defaultEmployerCode || '';
};

export const selectUser = (state: any) => state.auth.user;

export const selectIsAuthenticated = (state: any) => state.auth.isAuthenticated;

export default authSlice.reducer;
